import "./search-bar.scss";
import { HTMLProps, ReactNode, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import SearchIcon from "../../../../assets/svg/search-icon";
import { CrossIcon } from "../../../../assets/svg";
import { SearchInfo } from "../search-info/search-info";
import { SolicitorNotificationTagEnum } from "../../../../models/enum/solicitor-notification-tag.enum";
import useIsMobile from "../../../../hooks/use-is-mobile";

const REGEX = /^[a-zA-Z0-9#,()\- ]*$/;

interface SearchBarProps extends HTMLProps<HTMLInputElement> {
  isFetching: boolean;
  isError: boolean;
  latestSearchTerm: string;
  numberOfResults: number;
  tag: SolicitorNotificationTagEnum | null;
  onSearchCallback: () => void;
}

export interface SearchBarHandle {
  getSearchTerm: () => string;
  clear: () => void;
}

const SearchBar = forwardRef<SearchBarHandle, SearchBarProps>(function SearchBar(
  { isFetching, isError, disabled, latestSearchTerm, numberOfResults, tag, style, onSearchCallback, children, ...props },
  ref
) {
  // States
  const { isMobile } = useIsMobile();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isDisplayingResults, setIsDisplayingResults] = useState<boolean>(false);

  // Derived values
  const showClearButton = searchTerm.length !== 0;

  useImperativeHandle(
    ref,
    () => {
      return {
        getSearchTerm: () => searchTerm,
        clear: () => setSearchTerm(""),
      };
    },
    [searchTerm]
  );

  // Functions
  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newSearchTerm = event.target.value;
    if (!REGEX.test(newSearchTerm)) return;
    setSearchTerm(newSearchTerm);
  }

  function handleClear() {
    setSearchTerm("");
    onSearchCallback();
    setIsDisplayingResults(false);
  }

  function handleSearchByClick() {
    onSearchCallback();
    setIsDisplayingResults(true);
  }

  function handleSearchByEnter(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      onSearchCallback();
      setIsDisplayingResults(true);
    }
  }

  useEffect(() => {
    if (!searchTerm) {
      onSearchCallback();
      setIsDisplayingResults(false);
    }
  }, [searchTerm]);

  return (
    <>
      <div className={`polymer-search-bar__container ${disabled ? "polymer-search-bar__container-disabled" : ""}`} style={style}>
        <input
          type="textbox"
          className="polymer-search-bar__input"
          disabled={disabled}
          maxLength={50}
          onChange={handleChange}
          onKeyDown={handleSearchByEnter}
          pattern="[A-Za-z0-9#,()- ]"
          placeholder="Search by HDB Ref. and address"
          value={searchTerm}
          {...props}
        />
        {showClearButton ? (
          <>
            <button disabled={disabled} onClick={handleClear} className="polymer-search-bar__clear-button" style={{ paddingRight: 16 }}>
              <CrossIcon fillColor={disabled ? "#AAAAAA" : "#0C8188"} size={24} />
            </button>
          </>
        ) : null}
        {showClearButton ? <div className="polymer-search-bar__separator" /> : null}
        <button disabled={disabled} onClick={handleSearchByClick} className="polymer-search-bar__search-button">
          <SearchIcon fillColor={disabled ? "#AAAAAA" : "#0C8188"} size={24} />
        </button>
      </div>

      {children}

      {!isFetching && !isError && isDisplayingResults && searchTerm ? <SearchInfo numberOfResults={numberOfResults} tag={tag} searchTerm={searchTerm} /> : null}
    </>
  );
});

export { SearchBar };
